import { inject, injectable } from "inversify";
import type { IUserStore } from "@whatslly/ui-components/services/UserStore";
import { InjectUserStore } from "@whatslly/ui-components/services/UserStore";
import { UrlStore } from "../../../services/urlStore";
import { OrgConfigsStore } from "../../../services/OrgConfigsStore";
import type { IOrganizationStore } from "../../../services/IOrganizationStore";
import { InjectOrganizationStore } from "../../../services/IOrganizationStore";
import { action, computed, makeObservable, observable, runInAction } from "mobx";
import { deleteJSON, getJSON, postJSON, putJSON } from "@whatslly/commons/apiUtils";
import type { WhatsappBusinessApiWabaType } from "@whatslly/types/waba/WhatsappBusinessApiGetPhones";
import { WhatsappBusinessApiGetPhonesResponse, WhatsappBusinessApiPhone } from "@whatslly/types/waba/WhatsappBusinessApiGetPhones";
import { modalService } from "@whatslly/ui-components/modals";
import { ConfirmModal } from "@whatslly/ui-components/modals/ConfirmModal";
import { TFunction } from "i18next";
import Toast from "@whatslly/ui-components/v2/Toaster/Toast";
import { FrontLogger } from "@whatslly/commons/frontLogger/frontLogger";
import { hasPermission } from "@whatslly/commons/platformPermissions";
import { UserPermissions } from "@whatslly/commons/granularPermissions";
import { SMARTERS_SOLUTION_ID, WhatsappBusinessApiGetAccessTokenResponse } from "@whatslly/types/waba/WhatsappBusinessApiGetAccessToken";
import type { WhatsappBusinessApiDisconnectResponse } from "@whatslly/types/waba/WhatsappBusinessApiDisconnect";
import { SmartersTokenModal } from "./SmartersTokenModal";
import {
  WhatsappBusinessApiUpdatePhoneSmartersDataBody,
  WhatsappBusinessApiUpdatePhoneSmartersDataResponse,
} from "@whatslly/types/waba/WhatsappBusinessApiUpdatePhoneSmartersData";
import {
  WhatsappBusinessApiDeletePhoneSmartersDataBody,
  WhatsappBusinessApiDeletePhoneSmartersDataResponse,
} from "@whatslly/types/waba/WhatsappBusinessApiDeletePhoneSmartersData";
import { AssignPhoneToUserModal } from "./AssignPhoneToUserModal";
import {
  WhatsappBusinessApiAssignPhoneToUserBody,
  WhatsappBusinessApiAssignPhoneToUserResponse,
} from "@whatslly/types/waba/WhatsappBusinessApiAssignPhoneToUser";
import { VerifyPhoneNumberVerifyMethodModal } from "./VerifyPhoneNumberVerifyMethodModal";
import { VerifyPhoneNumberCodeModal } from "./VerifyPhoneNumberCodeModal/VerifyPhoneNumberCodeModal";
import { SERVER_ERROR_TYPES } from "@whatslly/types/ServerErrorTypes";
import { WhatsappBusinessApiRequestVerifyCodeResponse } from "@whatslly/types/waba/WhatsappBusinessApiRequestVerifyCode";
import { WhatsappBusinessApiVerifyCodeResponse } from "@whatslly/types/waba/WhatsappBusinessApiVerifyCode";
import type { TemplateLanguages } from "@whatslly/types/waba/WhatsappBusinessApiTemplate";
import { TemplatePurpose, WhatsappBusinessApiGetTemplatesResponse } from "@whatslly/types/waba/WhatsappBusinessApiTemplate";
import { WhatsappBusinessApiUpdateTemplateGroupsResponse } from "@whatslly/types/waba/WhatsappBusinessApiTemplateGroups";
import { WhatsappBusinessApiUpdateTemplatePurposeResponse } from "@whatslly/types/waba/WhatsappBusinessApiTemplatePurpose";
import { AddPhoneNumberWabaModal } from "./AddPhoneNumberWabaModal";
import { CreateTemplateModal } from "./CreateTemplateModal";
import { EditTemplateModal } from "./EditTemplateModal/EditTemplateModal";
import { EditTemplatePurposeModal } from "./EditTemplateModal/EditTemplatePurposeModal";
import { PreviewTemplateModal } from "./EditTemplateModal/PreviewTemplateModal";
import type { WhatsappBusinessAddPhoneNumberResponse } from "@whatslly/types/waba/WhatsappBusinessAddPhoneNumber";
import type { WhatsappBusinessApiCreateTemplateResponse } from "@whatslly/types/waba/WhatsappBusinessApiCreateTemplate";
import { parseOrReturnError } from "@whatslly/commons/parseOrReturnError";
import { WhatsappBusinessApiUploadHeaderMediaResponse } from "@whatslly/types/waba/WhatsappBusinessApiUploadHeaderMedia";
import { EMBEDDED_SIGNUP_LOGIN_GRACE_MS, listenForEmbeddedSignupSession } from "./embeddedSignupSession";

type FbLoginCbResponse = {
  authResponse: {
    userID: string | null;
    expiresIn: string | null;
    code: string;
  } | null;
  status: "connected" | "unknown";
};

declare const window: Window & {
  fbAsyncInit: VoidFunction;
  FB: {
    logout: VoidFunction;
    init: (data: { appId: string; cookie: boolean; xfbml: boolean; version: string }) => void;
    login: (
      cb: (response: FbLoginCbResponse) => void,
      config: {
        config_id: string;
        response_type: "code";
        override_default_response_type: true;
        extras: {
          featureType: "whatsapp_business_app_onboarding";
          sessionInfoVersion: "3";
          setup?: { solutionID: string };
        };
      }
    ) => void;
  };
};

const APP_CONFIG_ID = "391061790111164";
const APP_ID = "317936007434611";

function isSameWabaTemplate(left: TemplateLanguages, right: TemplateLanguages) {
  return left.name === right.name && left.wabaId === right.wabaId;
}

@injectable()
export class WhatsAppBusinessApiSettingsStore {
  isInitialized = false;
  isLoading = false;
  _phones: WhatsappBusinessApiPhone[] = [];
  _wabas: WhatsappBusinessApiWabaType[] = [];
  _templates: TemplateLanguages[] = [];
  _templateIds: Set<string> = new Set();
  connected = false;
  phonesUpdating = false;

  constructor(
    @inject(InjectUserStore) private readonly userStore: IUserStore,
    @inject(UrlStore) private readonly urlStore: UrlStore,
    @inject(InjectOrganizationStore) private readonly orgStore: IOrganizationStore,
    @inject(OrgConfigsStore) private readonly orgConfigsStore: OrgConfigsStore,
    @inject(FrontLogger) private readonly logger: FrontLogger
  ) {
    makeObservable(this, {
      hasWelcomeTemplate: computed,
      phonesUpdating: observable,
      isInitialized: observable,
      connectToWhatsApp: action.bound,
      disconnect: action.bound,
      _phones: observable.shallow,
      _wabas: observable.shallow,
      _templates: observable.shallow,
      _templateIds: observable,
      loadPhones: action.bound,
      filteredPhones: computed,
      filteredWabas: computed,
      connected: observable,
      isReadOnlySettingsUser: computed,
      hasSmartersPhone: computed,
      assignPhoneToUser: action.bound,
      toggleTemplate: action.bound,
      wabaSelectOptions: computed,
      isLoading: observable,
      createTemplate: action.bound,
      updateTemplateGroups: action.bound,
      updateTemplatePurpose: action.bound,
      openEditTemplateModal: action.bound,
      openEditTemplatePurposeModal: action.bound,
      openPreviewTemplateModal: action.bound,
    });
  }

  get isReadOnlySettingsUser() {
    return !hasPermission(this.userStore.user, UserPermissions["waba.update_settings"]);
  }

  async updatePhoneUsers(phoneNumberId: string, addUserIds: string[], removeUserIds: string[], t: TFunction) {
    const body: WhatsappBusinessApiAssignPhoneToUserBody = { orgId: (await this.getOrgId()) ?? "", addUserIds, removeUserIds };
    runInAction(() => {
      this.phonesUpdating = true;
    });
    const response = await putJSON({
      pod: await this.orgStore.wPod,
      urlSuffix: `/integrations/waba/phone/${phoneNumberId}/users`,
      etoken: this.userStore.etoken ?? "",
      body: JSON.stringify(body),
    })
      .then((res) => res.json() as Promise<WhatsappBusinessApiAssignPhoneToUserResponse>)
      .catch(() => ({ success: false }) as WhatsappBusinessApiAssignPhoneToUserResponse);

    if (response.success) {
      modalService.openModal(Toast, { text: t("whatsapp_business_api_settings_page.settings_saved_successfully") });
      this.loadPhones();
      return;
    }
    modalService.openModal(Toast, { text: t("whatsapp_business_api_settings_page.settings_saved_failed"), type: "error" });
  }

  async assignPhoneToUser(phoneNumberId: string, initUserIds: string[], t: TFunction) {
    const res = await modalService.openModal(AssignPhoneToUserModal, { initUserIds }).promise;
    if (!res) {
      return;
    }

    await this.updatePhoneUsers(
      phoneNumberId,
      res.selectedUserIds.filter((userId) => !initUserIds.includes(userId)),
      initUserIds.filter((userId) => !res.selectedUserIds.includes(userId)),
      t
    );
  }

  async updateSmartersToken(phoneNumberId: string, phoneNumber: number, wabaId: string, title: string, t: TFunction) {
    const token = await modalService.openModal(SmartersTokenModal, { title }).promise;
    if (token) {
      const body: WhatsappBusinessApiUpdatePhoneSmartersDataBody = {
        orgId: (await this.getOrgId()) ?? "",
        phoneNumberId,
        token,
        phoneNumber,
        wabaId,
      };
      const response = await putJSON({
        urlSuffix: `/integrations/waba/phone/${phoneNumberId}/smarters`,
        etoken: this.userStore.etoken ?? "",
        body: JSON.stringify(body),
        pod: await this.orgStore.wPod,
      })
        .then((res) => res.json() as Promise<WhatsappBusinessApiUpdatePhoneSmartersDataResponse>)
        .catch(() => ({ success: false }) as WhatsappBusinessApiUpdatePhoneSmartersDataResponse);

      if (response.success) {
        modalService.openModal(Toast, { text: t("whatsapp_business_api_settings_page.settings_saved_successfully") });
        this.loadPhones();
        return;
      }
      modalService.openModal(Toast, { text: t("whatsapp_business_api_settings_page.settings_saved_failed"), type: "error" });
    }
  }

  async verifyPhoneNumber(phoneNumberId: string, phoneNumber: string, wabaId: string, language: string, t: TFunction) {
    const methodResult = await modalService.openModal(VerifyPhoneNumberVerifyMethodModal).promise;
    if (!methodResult) {
      return;
    }
    const sendVerifyCodeResult = await postJSON<Response>({
      urlSuffix: `/integrations/waba/phone/${phoneNumberId}/request_code`,
      etoken: this.userStore.etoken ?? "",
      body: JSON.stringify({
        method: methodResult.value,
        orgId: await this.getOrgId(),
        wabaId,
        language,
      }),
    });
    const res: WhatsappBusinessApiRequestVerifyCodeResponse = await sendVerifyCodeResult.json();
    modalService.openModal(Toast, {
      text: res.success
        ? t("whatsapp_business_api_settings_page.code_sent_successfully_toast")
        : `${t("whatsapp_business_api_settings_page.code_sent_failed_toast")} ${
            res.error && res.error !== SERVER_ERROR_TYPES.UNKNOWN ? res.error : ""
          }`.trim(),
      type: res.success ? "regular" : "error",
    });
    const codeResult = await modalService.openModal(VerifyPhoneNumberCodeModal, { phone: phoneNumber }).promise;
    if (!codeResult) {
      return;
    }
    const verifyCodeRes = await postJSON<Response>({
      urlSuffix: `/integrations/waba/phone/${phoneNumberId}/verify_code`,
      etoken: this.userStore.etoken ?? "",
      body: JSON.stringify({
        method: methodResult.value,
        orgId: await this.getOrgId(),
        code: codeResult,
        wabaId,
      }),
    }).then((r) => parseOrReturnError<WhatsappBusinessApiVerifyCodeResponse>(r, this.logger));
    modalService.openModal(Toast, {
      text: verifyCodeRes.success
        ? t("whatsapp_business_api_settings_page.phone_verified_successfully_toast")
        : t("whatsapp_business_api_settings_page.phone_verified_failed_toast"),
      type: verifyCodeRes.success ? "regular" : "error",
    });

    if (verifyCodeRes.success) {
      this.loadPhones();
    }
  }

  async deleteSmartersToken(phoneNumberId: string, phoneNumber: string, t: TFunction, wabaId: string) {
    const res = await modalService.openModal(ConfirmModal, {
      title: t("whatsapp_business_api_settings_page.actions_phone_table.delete_smarters_token"),
      body: t("whatsapp_business_api_settings_page.smarters_modal.delete_token_message", { phoneNumber }),
    }).promise;
    if (res?.result) {
      const body: WhatsappBusinessApiDeletePhoneSmartersDataBody = {
        orgId: (await this.getOrgId()) ?? "",
        phoneNumberId,
        wabaId,
      };
      const response = await deleteJSON({
        urlSuffix: `/integrations/waba/phone/${phoneNumberId}/smarters`,
        etoken: this.userStore.etoken ?? "",
        body: JSON.stringify(body),
        pod: await this.orgStore.wPod,
      })
        .then((res) => res.json() as Promise<WhatsappBusinessApiDeletePhoneSmartersDataResponse>)
        .catch(() => ({ success: false }) as WhatsappBusinessApiDeletePhoneSmartersDataResponse);

      if (response.success) {
        modalService.openModal(Toast, { text: t("whatsapp_business_api_settings_page.settings_saved_successfully") });
        this.loadPhones();
        return;
      }
      modalService.openModal(Toast, { text: t("whatsapp_business_api_settings_page.settings_saved_failed"), type: "error" });
    }
  }

  get hasSmartersPhone() {
    return this._phones.some((phoneData) => phoneData.isSmarters);
  }

  get hasWelcomeTemplate() {
    return this._templates.some((template) => template.languages.some((lang) => lang.status === "APPROVED"));
  }

  get filteredPhones() {
    if (!this.hasWelcomeTemplate) {
      return [];
    }
    const term = this.getSearchTerm("phones").trim().toLowerCase();
    if (!term) {
      return this._phones;
    }
    const termNumbers = term.replace(/\D/g, "");
    if (!termNumbers) {
      return [];
    }
    return this._phones.filter((phoneData) => phoneData.phone?.replace(/\D/g, "").includes(termNumbers));
  }

  get filteredWabas() {
    const term = this.getSearchTerm("settings").trim().toLowerCase();
    if (!term) {
      return this._wabas;
    }
    return this._wabas.filter(
      (wabaData) =>
        wabaData.businessName.toLowerCase().includes(term) ||
        wabaData.wabaName.toLowerCase().includes(term) ||
        wabaData.wabaId.toLowerCase().includes(term)
    );
  }

  get wabaSelectOptions() {
    return this._wabas.map((item) => ({ label: item.wabaName, value: item.wabaId }));
  }

  getWabaAccountName(wabaId: string) {
    return this._wabas.find((waba) => waba.wabaId === wabaId)?.wabaName || wabaId;
  }

  get filteredTemplates() {
    const term = this.getSearchTerm("templates").trim().toLowerCase();
    if (!term) {
      return this._templates;
    }
    return this._templates.filter((template) => {
      const wabaName = this.getWabaAccountName(template.wabaId).toLowerCase();
      return template.name.toLowerCase().includes(term) || template.wabaId.toLowerCase().includes(term) || wabaName.includes(term);
    });
  }

  async getOrgId() {
    const org = await this.orgStore.selectedOrg;
    return org?.value ?? this.userStore.user?.organizationId;
  }

  private async fetchPhones(orgId: string) {
    return getJSON({ pod: await this.orgStore.wPod, urlSuffix: `/integrations/waba/phones?orgId=${orgId}`, etoken: this.userStore.etoken ?? "" })
      .then((res) => res.json() as Promise<WhatsappBusinessApiGetPhonesResponse>)
      .catch(() => ({ success: false }) as WhatsappBusinessApiGetPhonesResponse);
  }

  private async fetchTemplates(orgId: string) {
    return getJSON({ pod: await this.orgStore.wPod, urlSuffix: `/integrations/waba/templates?orgId=${orgId}`, etoken: this.userStore.etoken ?? "" })
      .then((res) => res.json() as Promise<WhatsappBusinessApiGetTemplatesResponse>)
      .catch(() => ({ success: false }) as WhatsappBusinessApiGetTemplatesResponse);
  }

  async loadPhones() {
    try {
      runInAction(() => {
        this.isLoading = true;
      });
      const orgId = await this.getOrgId();
      const authorized = hasPermission(this.userStore.user, UserPermissions["waba.update_settings"] | UserPermissions["waba.view_settings"]);
      if (!orgId || !authorized) {
        return;
      }
      runInAction(() => {
        this.phonesUpdating = true;
      });
      const [phones, templates] = await Promise.all([this.fetchPhones(orgId), this.fetchTemplates(orgId)]);

      runInAction(() => {
        this._phones = phones.success ? phones.phones : [];
        this._wabas = phones.success ? phones.wabas : [];
        this._templates = templates.success ? templates.templates : [];
        this._templateIds = new Set(templates.success ? templates.waba_template_ids : []);
        this.connected = phones.success ? phones.connected : false;
        this.isInitialized = true;
        this.phonesUpdating = false;
      });
    } finally {
      runInAction(() => {
        this.isLoading = false;
      });
    }
  }

  /* istanbul ignore next -- @preserve */
  loadFbScript() {
    return new Promise<void>((resolve, reject) => {
      const id = "facebook-jssdk";
      if (document.getElementById(id)) {
        resolve();
        return;
      }
      window.fbAsyncInit ??= function () {
        window.FB.init({
          appId: APP_ID,
          cookie: true,
          xfbml: true,
          version: "v24.0",
        });
        resolve();
      };
      const fjs = document.getElementsByTagName("script")[0];
      const js = document.createElement("script");
      js.id = id;
      js.async = true;
      js.src = "https://connect.facebook.net/en_US/sdk.js";
      js.onerror = () => reject();

      fjs.parentNode?.insertBefore(js, fjs);
    });
  }

  getSearchTerm(tab: string) {
    return this.urlStore.searchParams?.get(`${tab}_search`) ?? "";
  }

  setSearchTerm(search: string, tab: string) {
    this.urlStore.setSearchParams({ [`${tab}_search`]: search });
  }

  async addPhoneNumber(t: TFunction) {
    const res = await modalService.openModal(AddPhoneNumberWabaModal, { wabaSelectOptions: this.wabaSelectOptions }).promise;
    if (!res) {
      return;
    }

    const addPhoneResult = await postJSON<Response>({
      urlSuffix: `/integrations/waba/phone`,
      etoken: this.userStore.etoken ?? "",
      body: JSON.stringify({
        orgId: await this.getOrgId(),
        wabaId: res.wabaId,
        verifiedName: res.verifiedName,
        countryCode: res.countryCode,
        phoneNumber: res.phoneNumber,
      }),
    });
    const addPhoneRes: WhatsappBusinessAddPhoneNumberResponse = await addPhoneResult.json();
    modalService.openModal(Toast, {
      text: addPhoneRes.success
        ? t("whatsapp_business_api_settings_page.add_phone_successfully_toast")
        : t("whatsapp_business_api_settings_page.add_phone_failed_toast") +
          (addPhoneRes.error !== SERVER_ERROR_TYPES.UNKNOWN ? `: ${addPhoneRes.error}` : ""),
      type: addPhoneRes.success ? "regular" : "error",
    });

    if (addPhoneRes.success) {
      this.loadPhones();
    }
  }

  async uploadHeaderMedia(wabaId: string, file: File): Promise<{ handle: string }> {
    const formData = new FormData();
    formData.append("orgId", (await this.getOrgId()) ?? "");
    formData.append("wabaId", wabaId);
    formData.append("file", file);

    const res = await postJSON({
      urlSuffix: "/integrations/waba/templates/upload-header-media",
      etoken: this.userStore.etoken ?? "",
      body: formData,
      pod: await this.orgStore.wPod,
    }).then((r) => parseOrReturnError<WhatsappBusinessApiUploadHeaderMediaResponse>(r, this.logger));
    if (!res.success) {
      throw new Error(res.error || "Upload failed");
    }
    return { handle: res.handle };
  }

  async createTemplate(t: TFunction) {
    const res = await modalService.openModal(CreateTemplateModal, {
      wabaSelectOptions: this.wabaSelectOptions,
      onUploadHeaderMedia: (wabaId: string, file: File) => this.uploadHeaderMedia(wabaId, file),
    }).promise;
    if (!res) {
      return;
    }

    modalService.openModal(Toast, { text: t("whatsapp_business_api_settings_page.create_template_loading_toast") });

    const response = await postJSON({
      urlSuffix: `/integrations/waba/templates/create`,
      etoken: this.userStore.etoken ?? "",
      pod: await this.orgStore.wPod,
      body: JSON.stringify({
        orgId: await this.getOrgId(),
        wabaId: res.wabaId,
        name: res.name,
        language: res.language,
        category: res.category,
        components: res.components,
      }),
    })
      .then((r) => parseOrReturnError<WhatsappBusinessApiCreateTemplateResponse>(r, this.logger))
      .catch(() => ({ success: false }));
    if (!response.success) {
      modalService.openModal(Toast, { text: t("whatsapp_business_api_settings_page.create_template_failed_toast"), type: "error" });
      return;
    }
    modalService.openModal(Toast, {
      text: t("whatsapp_business_api_settings_page.create_template_success_toast"),
      type: "regular",
    });
    this.loadPhones();
  }

  async toggleTemplate(template: TemplateLanguages, t: TFunction) {
    const ids = template.languages.map((lang) => lang.id);
    const included = ids.every((lang) => this._templateIds.has(lang));
    const addTemplateIds: string[] = included ? [] : ids;
    const removeTemplateIds: string[] = included ? ids : [];
    const response = await putJSON({
      urlSuffix: `/integrations/waba/templates`,
      etoken: this.userStore.etoken ?? "",
      body: JSON.stringify({ orgId: await this.getOrgId(), addTemplateIds, removeTemplateIds }),
      pod: await this.orgStore.wPod,
    })
      .then((res) => res.json() as Promise<WhatsappBusinessApiAssignPhoneToUserResponse>)
      .catch(() => ({ success: false }) as WhatsappBusinessApiAssignPhoneToUserResponse);

    if (response.success) {
      addTemplateIds.forEach((id) => this._templateIds.add(id));
      removeTemplateIds.forEach((id) => this._templateIds.delete(id));
      modalService.openModal(Toast, { text: t("whatsapp_business_api_settings_page.settings_saved_successfully") });
      this.loadPhones();
      return;
    }
    modalService.openModal(Toast, { text: t("whatsapp_business_api_settings_page.settings_saved_failed"), type: "error" });
  }

  async updateTemplateGroups(template: TemplateLanguages, groupIds: string[], t: TFunction) {
    const orgId = (await this.getOrgId()) ?? "";
    const templateIds = template.languages.map((l) => l.id);
    const response: WhatsappBusinessApiUpdateTemplateGroupsResponse = await putJSON({
      pod: await this.orgStore.wPod,
      urlSuffix: `/integrations/waba/templates/groups`,
      etoken: this.userStore.etoken ?? "",
      body: JSON.stringify({ orgId, templateIds, groupIds }),
    })
      .then((r) => parseOrReturnError<WhatsappBusinessApiUpdateTemplateGroupsResponse>(r, this.logger))
      .catch(() => ({ success: false, error: "request failed" }));

    if (response.success) {
      runInAction(() => {
        this._templates = this._templates.map((tpl) => (isSameWabaTemplate(tpl, template) ? { ...tpl, groupIds } : tpl));
      });
      modalService.openModal(Toast, { text: t("whatsapp_business_api_settings_page.edit_template.save_success") });
      return;
    }
    modalService.openModal(Toast, { text: t("whatsapp_business_api_settings_page.edit_template.save_error"), type: "error" });
  }

  async updateTemplatePurpose(template: TemplateLanguages, purposes: TemplatePurpose[], t: TFunction) {
    const orgId = (await this.getOrgId()) ?? "";
    const templateIds = template.languages.map((l) => l.id);
    const response: WhatsappBusinessApiUpdateTemplatePurposeResponse = await putJSON({
      pod: await this.orgStore.wPod,
      urlSuffix: `/integrations/waba/templates/purpose`,
      etoken: this.userStore.etoken ?? "",
      body: JSON.stringify({ orgId, templateIds, purposes }),
    })
      .then((r) => parseOrReturnError<WhatsappBusinessApiUpdateTemplatePurposeResponse>(r, this.logger))
      .catch(() => ({ success: false, error: "request failed" }));

    if (response.success) {
      runInAction(() => {
        this._templates = this._templates.map((tpl) => (isSameWabaTemplate(tpl, template) ? { ...tpl, purposes } : tpl));
      });
      modalService.openModal(Toast, { text: t("whatsapp_business_api_settings_page.edit_template.save_success") });
      return;
    }
    modalService.openModal(Toast, { text: t("whatsapp_business_api_settings_page.edit_template.save_error"), type: "error" });
  }

  async openEditTemplateModal(template: TemplateLanguages, t: TFunction) {
    const result = await modalService.openModal(EditTemplateModal, { template, initialGroupIds: template.groupIds ?? [] }).promise;
    if (!result) return;
    await this.updateTemplateGroups(template, result.groupIds, t);
  }

  async openEditTemplatePurposeModal(template: TemplateLanguages, t: TFunction) {
    const result = await modalService.openModal(EditTemplatePurposeModal, { template, initialPurposes: template.purposes ?? [] }).promise;
    if (!result) return;
    await this.updateTemplatePurpose(template, result.purposes, t);
  }

  openPreviewTemplateModal(template: TemplateLanguages) {
    modalService.openModal(PreviewTemplateModal, { template });
  }

  /* istanbul ignore next -- @preserve */
  async disconnect(t: TFunction, wabaId: string, wabaName: string) {
    const res = await modalService.openModal(ConfirmModal, {
      title: t("whatsapp_business_api_settings_page.disconnect_modal_title"),
      body: t("whatsapp_business_api_settings_page.disconnect_modal_message", { wabaName }),
    }).promise;
    if (!res?.result) {
      return;
    }
    await this.loadFbScript();
    window.FB.logout();
    const response = await deleteJSON({
      pod: await this.orgStore.wPod,
      urlSuffix: `/integrations/waba/disconnect`,
      etoken: this.userStore.etoken ?? "",
      body: JSON.stringify({
        orgId: await this.getOrgId(),
        wabaId,
      }),
    });
    if (!response.ok) {
      this.logger.error("failed to disconnect");
      modalService.openModal(Toast, { text: t("whatsapp_business_api_settings_page.failed_to_disconnect"), type: "error" });
      return;
    }
    const body: WhatsappBusinessApiDisconnectResponse = await response.json();
    if (!body.success) {
      this.logger.error("failed to disconnect success = false");
      modalService.openModal(Toast, { text: t("whatsapp_business_api_settings_page.failed_to_disconnect"), type: "error" });
      return;
    }
    this.loadPhones();
  }

  /* istanbul ignore next -- @preserve */
  async connectToWhatsApp(t: TFunction) {
    const signup = listenForEmbeddedSignupSession();
    try {
      const [, signupWithPartner] = await Promise.all([
        this.loadFbScript(),
        this.orgConfigsStore.getObservableConfig("allow_embedded_signup_with_partner"),
      ]);
      const extras = {
        featureType: "whatsapp_business_app_onboarding" as const,
        sessionInfoVersion: "3" as const,
        ...(signupWithPartner ? { setup: { solutionID: SMARTERS_SOLUTION_ID } } : {}),
      };
      const code = await new Promise<string>((resolve, reject) => {
        window.FB.login(
          (response) => {
            if (response.authResponse) {
              const code = response.authResponse.code;
              resolve(code);
              return;
            }
            reject(new Error("User cancelled login or did not fully authorize."));
          },
          {
            config_id: APP_CONFIG_ID,
            response_type: "code",
            override_default_response_type: true,
            extras,
          }
        );
      });
      // FB.login and WA_EMBEDDED_SIGNUP are independent; the finish message can arrive after the popup closes.
      const session = await Promise.race([
        signup.session,
        new Promise<undefined>((resolve) => {
          setTimeout(() => resolve(undefined), EMBEDDED_SIGNUP_LOGIN_GRACE_MS);
        }),
      ]);
      if (!session?.wabaId) {
        throw new Error("missing selected WhatsApp business account");
      }
      const response = await postJSON({
        pod: await this.orgStore.wPod,
        urlSuffix: "/integrations/waba/connect",
        etoken: this.userStore.etoken ?? "",
        body: JSON.stringify({
          code,
          wabaId: session.wabaId,
          businessId: session.businessId,
          solutionId: signupWithPartner ? SMARTERS_SOLUTION_ID : undefined,
          orgId: await this.getOrgId(),
        }),
      });

      const body: WhatsappBusinessApiGetAccessTokenResponse = await response.json();
      window.FB.logout();
      if (!body.success) {
        this.logger.error("failed to connect success = false");
        modalService.openModal(Toast, { text: t("whatsapp_business_api_settings_page.failed_to_connect"), type: "error" });
        return;
      }
      this.isInitialized = false;
      this.loadPhones();
    } catch (err) {
      this.logger.error({ err }, "failed to connect");
      window.FB.logout();
      modalService.openModal(Toast, { text: t("whatsapp_business_api_settings_page.failed_to_connect"), type: "error" });
      return;
    } finally {
      signup.stop();
    }
  }
}
